'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface BrandHeaderProps {
  className?: string;
  activePage?: 'landing' | 'form' | 'results';
}

const GESTPUB_LOGO_URL =
  '/assets/images/6-1773229822720.png';

const BrandHeader = ({ className = '', activePage }: BrandHeaderProps) => {
  return (
    <header className={`w-full bg-white ${className}`} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Top bar — social + contact info (GestPub style) */}
      <div style={{ backgroundColor: '#272727' }}>
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
          {/* Left: brand tagline */}
          <div className="flex items-center gap-3">
            <span
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.72rem',
                color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.05em',
              }}
            >
              GestPub — Gestão Pública e Consultoria Autárquica
            </span>
          </div>
          {/* Right: contact info */}
          <div className="flex items-center gap-5">
            <a
              href="mailto:geral@gestpub.pt"
              className="flex items-center gap-1.5 transition-colors duration-200 hover:text-white"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#009688' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              geral@gestpub.pt
            </a>
            <a
              href="tel:+351214846546"
              className="flex items-center gap-1.5 transition-colors duration-200 hover:text-white"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#009688' }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l1.79-1.79a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              +351 214 846 546
            </a>
          </div>
        </div>
      </div>

      {/* Main nav bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-0 flex items-center justify-between">
          {/* Logo — links to homepage */}
          <Link
            href="/diagnostic-landing-page"
            className="flex items-center py-3 focus-visible:outline-none"
            aria-label="GestPub — Página inicial"
          >
            <AppImage
              src={GESTPUB_LOGO_URL}
              alt="GestPub — Gestão Pública e Consultoria Autárquica"
              width={180}
              height={48}
              className="h-11 w-auto object-contain"
              priority={true}
            />
          </Link>

          {/* Navigation links */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Navegação principal">
            <Link
              href="/diagnostic-landing-page"
              className={`nav-link px-4 py-5 text-sm font-medium transition-colors duration-200 ${
                activePage === 'landing' ?'text-primary border-b-2 border-primary' :'text-gray-700 hover:text-primary'
              }`}
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
            >
              Início
            </Link>
            <Link
              href="/multi-step-diagnostic-form"
              className={`nav-link px-4 py-5 text-sm font-medium transition-colors duration-200 ${
                activePage === 'form'
                  ? 'text-primary border-b-2 border-primary' :'text-gray-700 hover:text-primary'
              }`}
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
            >
              Diagnóstico
            </Link>
            <Link
              href="/results-and-recommendations"
              className={`nav-link px-4 py-5 text-sm font-medium transition-colors duration-200 ${
                activePage === 'results' ?'text-primary border-b-2 border-primary' :'text-gray-700 hover:text-primary'
              }`}
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
            >
              Resultados
            </Link>
            <a
              href="https://www.gestpub.pt/sobre-nos/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link px-4 py-5 text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
            >
              Sobre nós
            </a>
            <a
              href="https://www.gestpub.pt/contactos/"
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link px-4 py-5 text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200"
              style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}
            >
              Contactos
            </a>
            <Link
              href="/multi-step-diagnostic-form"
              className="ml-4 inline-flex items-center justify-center px-5 py-2.5 bg-primary text-white text-sm font-semibold transition-all duration-200 hover:bg-secondary"
              style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', border: '2px solid #009688' }}
            >
              Iniciar Diagnóstico
            </Link>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-primary transition-colors"
            aria-label="Menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};

export default BrandHeader;